import { useEffect, useMemo, useRef, useState } from "react";
import type { Condition, FieldDef } from "../../api/types";
import { FUNCTIONS, Icon } from "../../components/ui";

/** Constructeur de filtres piloté par le registre : tout champ filtrable (y compris dynamique) est proposé. */

export interface FieldOption {
  name: string; // entity.key ou champ virtuel
  label: string;
  group: string;
  type: "text" | "number" | "date" | "bool" | "array" | "point" | "virtual" | "enum";
  enumValues?: Record<string, string>;
}

const VIRTUAL: FieldOption[] = [
  { name: "has_valid_phone", label: "Téléphone valide", group: "Coordonnées", type: "virtual" },
  { name: "has_mobile", label: "Mobile valide", group: "Coordonnées", type: "virtual" },
  { name: "has_valid_email", label: "Email valide", group: "Coordonnées", type: "virtual" },
  { name: "has_website", label: "Site web renseigné", group: "Coordonnées", type: "virtual" },
];

const OPS: Record<FieldOption["type"], [string, string][]> = {
  text: [["eq", "est"], ["neq", "n'est pas"], ["contains", "contient"], ["starts_with", "commence par"], ["in", "est parmi"], ["is_not_empty", "est renseigné"], ["is_empty", "est vide"]],
  enum: [["eq", "est"], ["neq", "n'est pas"], ["in", "est parmi"], ["is_not_empty", "est renseigné"], ["is_empty", "est vide"]],
  number: [["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["eq", "="], ["between", "entre"], ["is_not_empty", "est renseigné"], ["is_empty", "est vide"]],
  date: [["gte", "à partir du"], ["lte", "jusqu'au"], ["between", "entre"], ["is_not_empty", "est renseignée"], ["is_empty", "est vide"]],
  bool: [["is_true", "oui"], ["is_false", "non"]],
  array: [["eq", "contient"], ["in", "contient l'un de"], ["neq", "ne contient pas"], ["is_not_empty", "est renseigné"], ["is_empty", "est vide"]],
  point: [["eq", "est"], ["contains", "contient"], ["is_not_empty", "est renseigné"], ["is_empty", "est vide"]],
  virtual: [["is_true", "oui"], ["is_false", "non"]],
};
const NO_VALUE = new Set(["is_empty", "is_not_empty", "is_true", "is_false"]);

export function fieldOptions(fields: FieldDef[]): FieldOption[] {
  const out: FieldOption[] = [];
  for (const f of fields) {
    if (!f.is_filterable || f.status !== "active") continue;
    const group = f.storage === "contact_point" ? "Coordonnées" : f.entity === "company" ? (f.origin === "core" ? "Entreprise" : "Entreprise · données ajoutées") : (f.origin === "core" ? "Contact" : "Contact · données ajoutées");
    let type: FieldOption["type"] = "text";
    if (f.storage === "contact_point") type = "point";
    else if (["integer", "decimal"].includes(f.data_type)) type = "number";
    else if (f.data_type === "date") type = "date";
    else if (f.data_type === "boolean") type = "bool";
    else if (["campaign_names", "call_center_names"].includes(f.key)) type = "array";
    else if (f.key === "function_code") type = "enum";
    out.push({ name: `${f.entity}.${f.key}`, label: f.label, group, type, enumValues: f.key === "function_code" ? FUNCTIONS : undefined });
  }
  return [...out, ...VIRTUAL];
}

export function describe(c: Condition, opts: FieldOption[]): string {
  const o = opts.find((x) => x.name === c.field);
  const label = o?.label ?? c.field;
  const op = OPS[o?.type ?? "text"].find(([k]) => k === c.op)?.[1] ?? c.op;
  if (NO_VALUE.has(c.op)) return o?.type === "virtual" || o?.type === "bool" ? `${label} : ${op}` : `${label} ${op}`;
  const show = (v: unknown) => (o?.enumValues?.[String(v)] ?? String(v));
  const v = Array.isArray(c.value) ? (c.op === "between" ? `${show(c.value[0])} et ${show(c.value[1])}` : c.value.map(show).join(", ")) : show(c.value);
  return `${label} ${op} ${v}`;
}

export function FilterEditor({ options, onAdd, onClose }: { options: FieldOption[]; onAdd: (c: Condition) => void; onClose: () => void }) {
  const [field, setField] = useState(options.find((o) => o.name === "company.department")?.name ?? options[0]?.name ?? "");
  const opt = options.find((o) => o.name === field);
  const ops = OPS[opt?.type ?? "text"];
  const [op, setOp] = useState(ops[0][0]);
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setOp(OPS[opt?.type ?? "text"][0][0]), [field, opt?.type]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const groups = useMemo(() => {
    const g: Record<string, FieldOption[]> = {};
    options.forEach((o) => (g[o.group] ??= []).push(o));
    return g;
  }, [options]);

  const cast = (s: string) => (opt?.type === "number" ? Number(s.replace(",", ".").replace(/\s/g, "")) : s.trim());
  const valid = NO_VALUE.has(op) || (op === "between" ? v1 !== "" && v2 !== "" : v1.trim() !== "") &&
    (opt?.type !== "number" || [v1, ...(op === "between" ? [v2] : [])].every((x) => isFinite(Number(x.replace(",", ".").replace(/\s/g, "")))));

  function add() {
    if (!valid) return;
    let value: unknown;
    if (op === "between") value = [cast(v1), cast(v2)];
    else if (op === "in") value = v1.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean).map(cast);
    else if (!NO_VALUE.has(op)) value = cast(v1);
    onAdd({ field, op, ...(value !== undefined ? { value } : {}) });
  }

  const inputType = opt?.type === "number" ? "text" : opt?.type === "date" ? "date" : "text";
  return (
    <div className="popover" ref={ref} role="dialog" aria-label="Ajouter un filtre">
      <label className="field">Champ
        <select className="select" value={field} onChange={(e) => { setField(e.target.value); setV1(""); setV2(""); }} autoFocus>
          {Object.entries(groups).map(([g, l]) => (
            <optgroup key={g} label={g}>{l.map((o) => <option key={o.name} value={o.name}>{o.label}</option>)}</optgroup>
          ))}
        </select>
      </label>
      <label className="field">Condition
        <select className="select" value={op} onChange={(e) => setOp(e.target.value)}>
          {ops.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
      </label>
      {!NO_VALUE.has(op) && (
        opt?.enumValues && op !== "in" ? (
          <label className="field">Valeur
            <select className="select" value={v1} onChange={(e) => setV1(e.target.value)}>
              <option value="">—</option>
              {Object.entries(opt.enumValues).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
            </select>
          </label>
        ) : (
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <label className="field" style={{ flex: 1 }}>{op === "between" ? "De" : op === "in" ? "Valeurs (séparées par des virgules)" : "Valeur"}
              <input className="input" type={inputType} inputMode={opt?.type === "number" ? "decimal" : undefined} value={v1}
                onChange={(e) => setV1(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            </label>
            {op === "between" && (
              <label className="field" style={{ flex: 1 }}>À
                <input className="input" type={inputType} value={v2} onChange={(e) => setV2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
              </label>
            )}
          </div>
        )
      )}
      <div className="row between">
        <button className="btn ghost" type="button" onClick={onClose}>Annuler</button>
        <button className="btn primary" type="button" onClick={add} disabled={!valid}><Icon name="plus" />Ajouter le filtre</button>
      </div>
    </div>
  );
}
